import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Chip from "@material-ui/core/Chip";
import Avatar from "@material-ui/core/Avatar";
import Badge from "@material-ui/core/Badge";
import Popover from "@material-ui/core/Popover";
import Collapse from "@material-ui/core/Collapse";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Skeleton from "@material-ui/lab/Skeleton";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { FormControl, InputLabel, Select, MenuItem } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";
import TuneIcon from "@material-ui/icons/Tune";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import SettingsIcon from "@material-ui/icons/Settings";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import { AUDIT_ACTIONS, AUDIT_SOURCES, AUDIT_STATUSES } from "@markedesk/core-contracts";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import BetaBadge from "../../components/BetaBadge";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { cachedApi } from "../../services/cachedApi";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import {
  AUDIT_TIMEZONE,
  actorOf,
  contextFieldsOf,
  dataFieldsOf,
  dataFilterFieldsOf,
  groupByDay,
  initialsOf,
  isSupport,
  searchStoppedEarly,
  sentenceParts,
  sentenceValuesOf,
  targetOf,
  toQueryParams
} from "./auditLogView";
import { subscribeAuditRecorded } from "./auditSocket";

const ACTIONS = Object.values(AUDIT_ACTIONS).flatMap(group => Object.values(group));
const STATUSES = Object.values(AUDIT_STATUSES);
const SOURCES = Object.values(AUDIT_SOURCES);
/** Filtro de texto só consulta depois de parar de digitar — não uma requisição por tecla. */
const TEXT_FILTER_DELAY_MS = 500;

/** Hoje em YYYY-MM-DD no fuso do navegador — o formato do input `type="date"`. */
const hoje = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

/**
 * A tela ABRE no dia atual, e limpar os filtros volta para ele — não para
 * "sem janela".
 *
 * Sem janela, um filtro que não casa com nada faz o serviço andar para trás dia
 * a dia até o teto de 30 blocos por resposta, e o "Continuar buscando" segue de
 * 30 em 30. Medido com 3 milhões de registros em 180 dias (`__benchmarks__/
 * auditListBenchmark.ts`), uma busca que não casa:
 *
 *   sem janela          1008 ms | 60 queries
 *   janela de 30 dias    989 ms | 60 queries   (30 dias = o teto de blocos)
 *   janela de 7 dias     251 ms | 16 queries
 *   SÓ O DIA ATUAL        23 ms |  2 queries   ← 44x
 *
 * O caso normal não muda (primeira página em 7 ms com ou sem janela): quem
 * precisa de mais fundo alarga o período de propósito, e o selo do período
 * aparece na barra de filtros para o padrão não ficar escondido.
 *
 * É função, e não constante de módulo: a tela aberta atravessando a meia-noite
 * congelaria o dia do import, e limpar os filtros voltaria para ontem.
 */
const filtrosPadrao = () => ({
  q: "",
  action: "",
  userId: "",
  status: "",
  source: "",
  support: false,
  from: hoje(),
  to: "",
  data: {}
});

/** Filtros que moram no popover "Mais filtros" (contam no selo do botão). */
const MORE_FILTER_KEYS = ["status", "source", "from", "to", "support"];

const RAIL = 20;

const useStyles = makeStyles(theme => {
  const statusColor = {
    success: theme.palette.success.main,
    denied: theme.palette.warning.main,
    failed: theme.palette.error.main
  };
  return {
    toolbar: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1)
    },
    search: { flex: "1 1 280px", minWidth: 220 },
    select: { minWidth: 170 },
    chips: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: theme.spacing(0.75),
      minHeight: 32,
      marginBottom: theme.spacing(1)
    },
    feed: {
      flex: 1,
      overflowY: "auto",
      padding: theme.spacing(0, 1, 2),
      ...theme.scrollbarStyles
    },
    day: {
      position: "sticky",
      top: 0,
      zIndex: 1,
      backgroundColor: theme.palette.background.paper,
      padding: theme.spacing(2, 1, 1),
      fontWeight: 600,
      fontSize: "0.95rem",
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${theme.palette.divider}`,
      "&::first-letter": { textTransform: "uppercase" }
    },
    list: { listStyle: "none", margin: 0, padding: 0 },
    entry: {
      display: "grid",
      gridTemplateColumns: `56px ${RAIL}px 36px minmax(0, 1fr) auto 32px`,
      alignItems: "start",
      columnGap: theme.spacing(1.5),
      width: "100%",
      padding: theme.spacing(1.25, 1),
      border: 0,
      background: "none",
      textAlign: "left",
      font: "inherit",
      color: "inherit",
      cursor: "pointer",
      borderRadius: theme.shape.borderRadius,
      "&:hover": { backgroundColor: theme.palette.action.hover },
      "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: `44px ${RAIL}px minmax(0, 1fr) 32px`,
        "& $avatar, & $status": { display: "none" }
      }
    },
    time: {
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.secondary,
      fontSize: "0.85rem",
      paddingTop: 7,
      textAlign: "right"
    },
    // A linha do tempo: o traço corre pela altura do item, o ponto tem a cor do resultado.
    rail: {
      position: "relative",
      alignSelf: "stretch",
      "&::before": {
        content: '""',
        position: "absolute",
        left: RAIL / 2 - 1,
        top: -theme.spacing(1.25),
        bottom: -theme.spacing(1.25),
        width: 2,
        backgroundColor: theme.palette.divider
      }
    },
    railFirst: { "&::before": { top: 14 } },
    railLast: { "&::before": { bottom: `calc(100% - 14px)` } },
    dot: {
      position: "absolute",
      left: RAIL / 2 - 6,
      top: 9,
      width: 12,
      height: 12,
      borderRadius: "50%",
      border: `2px solid ${theme.palette.background.paper}`
    },
    dot_success: { backgroundColor: statusColor.success },
    dot_denied: { backgroundColor: statusColor.denied },
    dot_failed: { backgroundColor: statusColor.failed },
    avatar: {
      width: 32,
      height: 32,
      fontSize: "0.8rem",
      fontWeight: 600,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText
    },
    avatarMuted: {
      backgroundColor: theme.palette.action.selected,
      color: theme.palette.text.secondary
    },
    sentence: {
      fontSize: "0.95rem",
      lineHeight: 1.5,
      color: theme.palette.text.primary,
      "& strong": { fontWeight: 600 }
    },
    meta: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: theme.spacing(1),
      marginTop: 2,
      color: theme.palette.text.secondary,
      fontSize: "0.8rem"
    },
    status: { paddingTop: 4 },
    statusChip: { fontWeight: 600, color: "#fff" },
    status_denied: { backgroundColor: statusColor.denied },
    status_failed: { backgroundColor: statusColor.failed },
    chevron: {
      marginTop: 2,
      transition: theme.transitions.create("transform", { duration: theme.transitions.duration.shortest }),
      "@media (prefers-reduced-motion: reduce)": { transition: "none" }
    },
    chevronOpen: { transform: "rotate(180deg)" },
    details: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
      gap: theme.spacing(2),
      margin: theme.spacing(0, 1, 1.5, `${56 + RAIL + 36 + 12 * 3}px`),
      padding: theme.spacing(1.5, 2),
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.action.hover,
      [theme.breakpoints.down("xs")]: { marginLeft: theme.spacing(1) }
    },
    detailsTitle: { fontWeight: 600, fontSize: "0.8rem", marginBottom: theme.spacing(0.5) },
    dl: {
      display: "grid",
      gridTemplateColumns: "max-content minmax(0, 1fr)",
      columnGap: theme.spacing(2),
      rowGap: theme.spacing(0.5),
      margin: 0,
      fontSize: "0.85rem",
      "& dt": { color: theme.palette.text.secondary },
      "& dd": { margin: 0, overflowWrap: "anywhere" }
    },
    footer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(3, 2),
      color: theme.palette.text.secondary,
      textAlign: "center"
    },
    newBanner: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      padding: theme.spacing(0.75, 1.5),
      marginBottom: theme.spacing(1),
      borderRadius: theme.shape.borderRadius,
      border: `1px solid ${theme.palette.primary.main}`,
      color: theme.palette.text.primary
    },
    // O tema limita todo Popover do app a 300px com !important (App.js,
    // MuiPopover.paper). Este precisa da largura do conteúdo: sobrescreve só
    // aqui, com !important e seletor mais específico (classe dobrada), para não
    // mexer nos outros popovers do app.
    popoverPaper: { "&&": { maxWidth: "calc(100vw - 32px) !important" } },
    popover: {
      display: "grid",
      gap: theme.spacing(2),
      padding: theme.spacing(2.5),
      width: 420,
      maxWidth: "calc(100vw - 32px)",
      boxSizing: "border-box"
    },
    // Campo de data nativo precisa de ~170px (data + ícone do calendário):
    // lado a lado só quando couber, senão um embaixo do outro.
    dates: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
      gap: theme.spacing(1.5)
    }
  };
});

/** Tradução com fallback no próprio valor (item novo do catálogo sem tradução ainda). */
const label = (key, fallback) => (i18n.exists(key) ? i18n.t(key) : fallback);

const formatTime = createdAt =>
  new Date(createdAt).toLocaleTimeString(undefined, { timeZone: AUDIT_TIMEZONE, hour: "2-digit", minute: "2-digit" });

const formatDay = day =>
  new Date(`${day}T12:00:00Z`).toLocaleDateString(undefined, {
    timeZone: AUDIT_TIMEZONE,
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric"
  });

const formatShortDay = day =>
  new Date(`${day}T12:00:00Z`).toLocaleDateString(undefined, { timeZone: AUDIT_TIMEZONE, day: "2-digit", month: "2-digit" });

const actorText = (record, nameOf) => {
  const actor = actorOf(record, nameOf);
  if (actor.kind === "user") return actor.name || i18n.t("auditLogs.actor.unknownUser", { id: actor.id });
  if (actor.kind === "api") return i18n.t("auditLogs.actor.api", { token: actor.token });
  return `${i18n.t("auditLogs.actor.system")} (${label(`auditLogs.sources.${actor.source}`, actor.source)})`;
};

const entityLabel = entity => label(`auditLogs.entities.${entity}`, entity);

const ActorAvatar = ({ record, nameOf, classes }) => {
  const actor = actorOf(record, nameOf);
  if (actor.kind === "user") {
    return <Avatar className={classes.avatar}>{initialsOf(actor.name || "")}</Avatar>;
  }
  return (
    <Avatar className={`${classes.avatar} ${classes.avatarMuted}`}>
      {actor.kind === "api" ? <VpnKeyIcon fontSize="small" /> : <SettingsIcon fontSize="small" />}
    </Avatar>
  );
};

/** A frase da ação, com autor e alvo em negrito. Sem tradução: "autor · ação · alvo" legível. */
const Sentence = ({ record, nameOf, classes }) => {
  const key = `auditLogs.sentences.${record.action}`;
  const who = actorText(record, nameOf);
  if (!i18n.exists(key)) {
    return (
      <Typography className={classes.sentence}>
        <strong>{who}</strong> {label(`auditLogs.actions.${record.action}`, record.action)}{" "}
        <strong>{targetOf(record, entityLabel)}</strong>
      </Typography>
    );
  }
  const parts = sentenceParts((k, values) => i18n.t(k, values), key, sentenceValuesOf(record, who));
  return (
    <Typography className={classes.sentence}>
      {parts.map((part, i) => (part.strong ? <strong key={i}>{part.text}</strong> : <span key={i}>{part.text}</span>))}
    </Typography>
  );
};

const Details = ({ record, classes }) => {
  const data = dataFieldsOf(record).map(f => [label(f.labelKey, f.field), f.value]);
  const { ip, userAgent, route, serverId, actorCompanyId } = contextFieldsOf(record);
  const context = [
    [i18n.t("auditLogs.details.source"), label(`auditLogs.sources.${record.source}`, record.source)],
    [i18n.t("auditLogs.details.route"), route],
    [i18n.t("auditLogs.details.ip"), ip],
    [i18n.t("auditLogs.details.userAgent"), userAgent],
    [i18n.t("auditLogs.details.server"), serverId],
    [i18n.t("auditLogs.details.actorCompany"), actorCompanyId]
  ].filter(([, value]) => value !== undefined && value !== null && value !== "");

  const block = (title, rows) =>
    rows.length > 0 && (
      <div>
        <Typography className={classes.detailsTitle}>{title}</Typography>
        <dl className={classes.dl}>
          {rows.map(([name, value]) => (
            <React.Fragment key={name}>
              <dt>{name}</dt>
              <dd>{value}</dd>
            </React.Fragment>
          ))}
        </dl>
      </div>
    );

  return (
    <div className={classes.details}>
      {block(i18n.t("auditLogs.details.data"), data)}
      {block(i18n.t("auditLogs.details.context"), context)}
    </div>
  );
};

const Entry = ({ record, first, last, open, onToggle, nameOf, classes }) => {
  const railClass = [classes.rail, first && classes.railFirst, last && classes.railLast].filter(Boolean).join(" ");
  const detailsId = `audit-details-${record.id}`;
  return (
    <li>
      <button type="button" className={classes.entry} onClick={onToggle} aria-expanded={open} aria-controls={detailsId}>
        <time className={classes.time} dateTime={record.createdAt}>
          {formatTime(record.createdAt)}
        </time>
        <span className={railClass} aria-hidden="true">
          <span className={`${classes.dot} ${classes[`dot_${record.status}`] || ""}`} />
        </span>
        <ActorAvatar record={record} nameOf={nameOf} classes={classes} />
        <span>
          <Sentence record={record} nameOf={nameOf} classes={classes} />
          <span className={classes.meta}>
            <span>{label(`auditLogs.actions.${record.action}`, record.action)}</span>
            {isSupport(record) && <Chip size="small" variant="outlined" color="primary" label={i18n.t("auditLogs.support")} />}
          </span>
        </span>
        <span className={classes.status}>
          {record.status !== "success" && (
            <Chip
              size="small"
              className={`${classes.statusChip} ${classes[`status_${record.status}`] || ""}`}
              label={label(`auditLogs.statuses.${record.status}`, record.status)}
            />
          )}
        </span>
        <Tooltip title={i18n.t(open ? "auditLogs.hideDetails" : "auditLogs.showDetails")}>
          <ExpandMoreIcon className={`${classes.chevron} ${open ? classes.chevronOpen : ""}`} color="action" />
        </Tooltip>
      </button>
      <Collapse in={open} timeout="auto" unmountOnExit id={detailsId}>
        <Details record={record} classes={classes} />
      </Collapse>
    </li>
  );
};

/** Campo de texto que só aplica o filtro depois de parar de digitar. */
const DelayedTextFilter = ({ value, onApply, ...props }) => {
  const [draft, setDraft] = useState(value);
  const apply = useRef(onApply);
  apply.current = onApply;
  useEffect(() => setDraft(value), [value]);
  useEffect(() => {
    if (draft === value) return undefined;
    const timer = setTimeout(() => apply.current(draft), TEXT_FILTER_DELAY_MS);
    return () => clearTimeout(timer);
  }, [draft, value]);

  return (
    <TextField
      {...props}
      size="small"
      variant="outlined"
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onKeyDown={e => e.key === "Enter" && draft !== value && onApply(draft)}
    />
  );
};

const LoadingRows = ({ classes }) => (
  <ul className={classes.list} aria-hidden="true">
    {[0, 1, 2, 3].map(i => (
      <li key={i} className={classes.entry} style={{ cursor: "default" }}>
        <Skeleton width={40} />
        <span />
        <Skeleton variant="circle" width={32} height={32} />
        <span>
          <Skeleton width="70%" />
          <Skeleton width="30%" />
        </span>
      </li>
    ))}
  </ul>
);

const AuditLogs = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const companyId = Number(user?.companyId);

  const [filters, setFilters] = useState(filtrosPadrao);
  const [records, setRecords] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [scannedUntil, setScannedUntil] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [users, setUsers] = useState([]);
  const [moreAnchor, setMoreAnchor] = useState(null);
  // Registros gravados desde a última carga (socket da sala da auditoria).
  const [newCount, setNewCount] = useState(0);
  // Resposta que chega depois de o filtro mudar não pode entrar na lista nova.
  const generation = useRef(0);

  useEffect(() => {
    api
      .get("/users/list")
      .then(({ data }) => setUsers(Array.isArray(data) ? data : []))
      .catch(() => setUsers([]));
  }, []);

  const load = useCallback(
    async (cursor, gen) => {
      if (!companyId) return;
      setLoading(true);
      try {
        const params = toQueryParams({ ...filters, cursor });
        const page = await cachedApi.auditLogs.list(companyId, params);
        if (gen !== generation.current) return;
        setRecords(prev => (cursor ? [...prev, ...page.records] : page.records));
        setNextCursor(page.nextCursor);
        setScannedUntil(page.scannedUntil);
      } catch (err) {
        if (gen === generation.current) toastError(err);
      } finally {
        if (gen === generation.current) {
          setLoading(false);
          setLoadedOnce(true);
        }
      }
    },
    [companyId, filters]
  );

  const reload = useCallback(() => {
    generation.current += 1;
    setRecords([]);
    setNextCursor(null);
    setScannedUntil(null);
    setExpanded(null);
    setNewCount(0);
    load(undefined, generation.current);
  }, [load]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Não recarrega sozinho: quem está lendo um registro não perde o lugar.
  // Só conta e oferece atualizar.
  useEffect(() => subscribeAuditRecorded(socket, () => setNewCount(count => count + 1)), [socket]);

  const loadMore = () => load(nextCursor, generation.current);

  const handleRefresh = () => {
    cachedApi.auditLogs.invalidate("auditoria:atualizar");
    reload();
  };

  const handleScroll = e => {
    if (!nextCursor || loading || records.length === 0) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 200) < clientHeight) loadMore();
  };

  const setFilter = (name, value) =>
    // Trocar a ação troca a estrutura: filtros de `data` da anterior não valem mais.
    setFilters(prev => ({ ...prev, [name]: value, ...(name === "action" ? { data: {} } : {}) }));

  const setDataFilter = (field, value) => setFilters(prev => ({ ...prev, data: { ...prev.data, [field]: value } }));

  const usersById = useMemo(() => new Map(users.map(u => [u.id, u])), [users]);
  const nameOf = useCallback(id => usersById.get(id)?.name || null, [usersById]);
  const moreCount = MORE_FILTER_KEYS.filter(key => !!filters[key]).length;

  /** Filtros ativos como chips removíveis — o que está valendo fica sempre à vista. */
  const activeChips = [
    filters.q && { key: "q", text: `"${filters.q}"`, clear: () => setFilter("q", "") },
    filters.action && {
      key: "action",
      text: label(`auditLogs.actions.${filters.action}`, filters.action),
      clear: () => setFilter("action", "")
    },
    ...Object.entries(filters.data)
      .filter(([, value]) => value)
      .map(([field, value]) => ({
        key: `data.${field}`,
        text: `${label(`auditLogs.fields.${filters.action.split(".")[0]}.${field}`, field)}: ${value}`,
        clear: () => setDataFilter(field, "")
      })),
    filters.userId && {
      key: "userId",
      text: usersById.get(filters.userId)?.name || `#${filters.userId}`,
      clear: () => setFilter("userId", "")
    },
    filters.status && {
      key: "status",
      text: label(`auditLogs.statuses.${filters.status}`, filters.status),
      clear: () => setFilter("status", "")
    },
    filters.source && {
      key: "source",
      text: label(`auditLogs.sources.${filters.source}`, filters.source),
      clear: () => setFilter("source", "")
    },
    (filters.from || filters.to) && {
      key: "period",
      text: [filters.from && formatShortDay(filters.from), filters.to && formatShortDay(filters.to)].filter(Boolean).join(" – "),
      clear: () => setFilters(prev => ({ ...prev, from: "", to: "" }))
    },
    filters.support && { key: "support", text: i18n.t("auditLogs.filters.supportOnly"), clear: () => setFilter("support", false) }
  ].filter(Boolean);

  /**
   * Há algo além do padrão (que é o dia atual)? O selo do período aparece
   * sempre — é o padrão ficando à vista —, mas "Limpar filtros" apareceria
   * junto e, clicado, voltaria para o mesmo dia: pareceria não fazer nada.
   * Para alargar a janela, o caminho é remover o selo do período.
   */
  const temFiltroAlemDoPadrao =
    activeChips.some(chip => chip.key !== "period") || !!filters.to || filters.from !== hoje();

  const selectFilter = (name, options, group, fullWidth = false) => (
    <FormControl variant="outlined" size="small" className={fullWidth ? undefined : classes.select} fullWidth={fullWidth}>
      <InputLabel>{i18n.t(`auditLogs.filters.${name}`)}</InputLabel>
      <Select value={filters[name]} onChange={e => setFilter(name, e.target.value)} label={i18n.t(`auditLogs.filters.${name}`)}>
        <MenuItem value="">{i18n.t("auditLogs.filters.all")}</MenuItem>
        {options.map(option => (
          <MenuItem key={option} value={option}>
            {label(`auditLogs.${group}.${option}`, option)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const days = groupByDay(records);
  const stoppedEarly = searchStoppedEarly(records, nextCursor);

  return (
    <MainContainer>
      <MainHeader>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Title>{i18n.t("auditLogs.title")}</Title>
          {/* A auditoria está ganhando ações novas a cada tarefa (#4119, #4454):
              o selo avisa que o catálogo ainda cresce. Mesmo componente da tela
              de Tarefas — não é um selo próprio. */}
          <BetaBadge />
        </div>
        <MainHeaderButtonsWrapper>
          <Button variant="outlined" color="primary" startIcon={<RefreshIcon />} onClick={handleRefresh}>
            {i18n.t("auditLogs.refresh")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className={classes.toolbar}>
        <DelayedTextFilter
          className={classes.search}
          placeholder={i18n.t("auditLogs.searchPlaceholder")}
          inputProps={{ "aria-label": i18n.t("auditLogs.searchPlaceholder") }}
          value={filters.q}
          onApply={value => setFilter("q", value.trim())}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            )
          }}
        />
        {selectFilter("action", ACTIONS, "actions")}
        {dataFilterFieldsOf(filters.action).map(({ field, labelKey }) => (
          <DelayedTextFilter
            key={`${filters.action}:${field}`}
            label={label(labelKey, field)}
            value={filters.data[field] || ""}
            onApply={value => setDataFilter(field, value.trim())}
          />
        ))}
        <Autocomplete
          className={classes.select}
          size="small"
          options={users}
          getOptionLabel={option => option.name || `#${option.id}`}
          value={usersById.get(filters.userId) || null}
          onChange={(_e, option) => setFilter("userId", option ? option.id : "")}
          renderInput={params => <TextField {...params} variant="outlined" label={i18n.t("auditLogs.filters.person")} />}
        />
        <Badge color="primary" badgeContent={moreCount} invisible={moreCount === 0}>
          <Button variant="outlined" startIcon={<TuneIcon />} onClick={e => setMoreAnchor(e.currentTarget)}>
            {i18n.t("auditLogs.moreFilters")}
          </Button>
        </Badge>
        <Popover
          open={!!moreAnchor}
          anchorEl={moreAnchor}
          onClose={() => setMoreAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{ className: classes.popoverPaper }}
        >
          <div className={classes.popover}>
            {selectFilter("status", STATUSES, "statuses", true)}
            {selectFilter("source", SOURCES, "sources", true)}
            <div className={classes.dates}>
              <TextField
                type="date"
                fullWidth
                size="small"
                variant="outlined"
                label={i18n.t("auditLogs.filters.from")}
                InputLabelProps={{ shrink: true }}
                value={filters.from}
                onChange={e => setFilter("from", e.target.value)}
              />
              <TextField
                type="date"
                fullWidth
                size="small"
                variant="outlined"
                label={i18n.t("auditLogs.filters.to")}
                InputLabelProps={{ shrink: true }}
                value={filters.to}
                onChange={e => setFilter("to", e.target.value)}
              />
            </div>
            <FormControlLabel
              control={
                <Switch checked={filters.support} onChange={e => setFilter("support", e.target.checked)} color="primary" />
              }
              label={i18n.t("auditLogs.filters.supportOnly")}
            />
          </div>
        </Popover>
      </div>

      {activeChips.length > 0 && (
        <div className={classes.chips}>
          {activeChips.map(chip => (
            <Chip key={chip.key} size="small" label={chip.text} onDelete={chip.clear} />
          ))}
          {temFiltroAlemDoPadrao && (
          <Button size="small" onClick={() => setFilters(filtrosPadrao())}>
            {i18n.t("auditLogs.clearFilters")}
          </Button>
          )}
        </div>
      )}

      {newCount > 0 && (
        <div className={classes.newBanner} role="status">
          <Typography variant="body2">{i18n.t("auditLogs.newRecords", { count: newCount })}</Typography>
          <Button size="small" color="primary" variant="contained" startIcon={<RefreshIcon />} onClick={handleRefresh}>
            {i18n.t("auditLogs.refresh")}
          </Button>
        </div>
      )}

      <Paper variant="outlined" className={classes.feed} onScroll={handleScroll}>
        {days.map(group => (
          <section key={group.day} aria-label={formatDay(group.day)}>
            <Typography component="h2" className={classes.day}>
              {formatDay(group.day)}
            </Typography>
            <ul className={classes.list}>
              {group.records.map((record, i) => (
                <Entry
                  key={record.id}
                  record={record}
                  first={i === 0}
                  last={i === group.records.length - 1}
                  open={expanded === record.id}
                  onToggle={() => setExpanded(expanded === record.id ? null : record.id)}
                  nameOf={nameOf}
                  classes={classes}
                />
              ))}
            </ul>
          </section>
        ))}

        {loading && <LoadingRows classes={classes} />}

        {loadedOnce && !loading && (
          <div className={classes.footer}>
            {stoppedEarly && (
              <>
                <Typography>
                  {i18n.t("auditLogs.stoppedUntil", { day: scannedUntil ? formatShortDay(scannedUntil) : "" })}
                </Typography>
                <Button variant="outlined" color="primary" onClick={loadMore}>
                  {i18n.t("auditLogs.keepSearching")}
                </Button>
              </>
            )}
            {!stoppedEarly && records.length === 0 && (
              <>
                <Typography>{i18n.t(activeChips.length > 0 ? "auditLogs.emptyFiltered" : "auditLogs.empty")}</Typography>
                {activeChips.length > 0 && (
                  <Button variant="outlined" onClick={() => setFilters(filtrosPadrao())}>
                    {i18n.t("auditLogs.clearFilters")}
                  </Button>
                )}
              </>
            )}
            {records.length > 0 && nextCursor && (
              <Button onClick={loadMore}>{i18n.t("auditLogs.loadMore")}</Button>
            )}
            {records.length > 0 && !nextCursor && <Typography variant="body2">{i18n.t("auditLogs.end")}</Typography>}
          </div>
        )}
      </Paper>
    </MainContainer>
  );
};

export default AuditLogs;
